// Supplier portal API helper (Lane 1331, Phase 2 slice 2A).
//
// The portal is a separate app area for supplier people. It keeps its own
// token under its own storage key and never touches the buyer AuthContext,
// `authFetch`, or the buyer's `authToken` / `authUser` keys, so a buyer
// session can never send a portal token and the reverse.
import { API_BASE } from '../../config';

const TOKEN_KEY = 'sourcingPortalToken';
const BUYER_KEY = 'sourcingPortalBuyer';
export const PORTAL_HOME = '/sourcing-portal';
export const PORTAL_LOGIN = '/sourcing-portal/login';
export const PORTAL_FORGOT = '/sourcing-portal/forgot';

// Every storage access is wrapped: private windows and blocked site data throw.
export function getPortalToken() {
  try { return window.localStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
}
export function setPortalToken(token) {
  try { window.localStorage.setItem(TOKEN_KEY, token); } catch (e) { /* storage unavailable */ }
}
export function clearPortalToken() {
  try { window.localStorage.removeItem(TOKEN_KEY); } catch (e) { /* storage unavailable */ }
  try { window.sessionStorage.removeItem(BUYER_KEY); } catch (e) { /* storage unavailable */ }
}
export function getChosenBuyer() {
  try { return window.sessionStorage.getItem(BUYER_KEY); } catch (e) { return null; }
}
export function setChosenBuyer(membershipId) {
  try { window.sessionStorage.setItem(BUYER_KEY, membershipId); } catch (e) { /* storage unavailable */ }
}
// "How this works" is shown the first time a person opens an event (Lane 1394),
// remembered per event in this browser. Storage that throws counts as not seen.
const HOWTO_KEY = 'sourcingPortalHowto:';
export function howtoSeen(eventId) {
  try { return window.localStorage.getItem(HOWTO_KEY + eventId) === '1'; } catch (e) { return false; }
}
export function setHowtoSeen(eventId) {
  try { window.localStorage.setItem(HOWTO_KEY + eventId, '1'); } catch (e) { /* storage unavailable */ }
}

export class PortalError extends Error {
  constructor(status, detail, fallback = 'Something went wrong. Try again.') {
    const message = (detail && typeof detail === 'object' && detail.message)
      || (typeof detail === 'string' && detail !== 'Not found' ? detail : fallback);
    super(message);
    this.status = status;
    this.code = detail && typeof detail === 'object' ? detail.code : undefined;
    // Row-level problems some routes return with the code (draft saves, bid files).
    this.errors = detail && typeof detail === 'object' && Array.isArray(detail.errors)
      ? detail.errors : [];
  }
}

// A fresh key for one user action (submit, withdraw, decline). The caller keeps
// it and sends the same key when it retries that same action.
export function newActionKey() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function goToLogin() {
  if (window.location.pathname !== PORTAL_LOGIN) window.location.assign(PORTAL_LOGIN);
}

// `auth: true` marks a signed-in route: it sends the token, and a 401 means
// the session is over (clear the token, go to sign-in). Public routes (inspect,
// accept, login) use 401 for a wrong password, so they never redirect.
// `form` sends a multipart body instead of JSON; `blob` returns the response
// body as a file instead of reading JSON; `key` sends an Idempotency-Key.
async function request(method, path, { body, auth = true, params, form, blob = false, key } = {}) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (key) headers['Idempotency-Key'] = key;
  if (auth) {
    const token = getPortalToken();
    if (!token) {
      goToLogin();
      throw new PortalError(401, null, 'Sign in to continue.');
    }
    headers.Authorization = `Bearer ${token}`;
  }
  const qs = params ? new URLSearchParams(Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')).toString() : '';
  let response;
  try {
    response = await fetch(`${API_BASE}/api/sourcing-portal${path}${qs ? `?${qs}` : ''}`, {
      method,
      headers,
      body: form !== undefined ? form : (body === undefined ? undefined : JSON.stringify(body)),
    });
  } catch (e) {
    throw new PortalError(0, null, 'Unable to reach Wingman. Check your connection and try again.');
  }
  if (blob && response.ok) {
    try { return await response.blob(); } catch (e) {
      throw new PortalError(0, null, 'The download stopped. Try again.');
    }
  }
  let data = null;
  try { data = await response.json(); } catch (e) { data = null; }
  if (!response.ok) {
    if (auth && response.status === 401) {
      clearPortalToken();
      goToLogin();
    }
    throw new PortalError(response.status, data && data.detail);
  }
  return data;
}

const enc = encodeURIComponent;

export const portalApi = {
  inspect: (token) => request('POST', '/invitations/inspect', { body: { token }, auth: false }),
  accept: (token, password, fullName) => request('POST', '/invitations/accept', {
    body: { token, password, full_name: fullName }, auth: false,
  }),
  login: (email, password) => request('POST', '/auth/login', { body: { email, password }, auth: false }),
  // Lane 1442: forgot password. The request answers the same for any email.
  requestReset: (email) => request('POST', '/auth/password-reset', { body: { email }, auth: false }),
  confirmReset: (token, password) => request('POST', '/auth/password-reset/confirm', {
    body: { token, password }, auth: false,
  }),
  logout: () => request('POST', '/auth/logout'),
  me: () => request('GET', '/me'),
  events: (membershipId) => request('GET', `/buyers/${enc(membershipId)}/events`),
  notices: (membershipId) => request('GET', `/buyers/${enc(membershipId)}/notices`),
  readNotice: (noticeId) => request('POST', `/notices/${enc(noticeId)}/read`),
  event: (eventId) => request('GET', `/events/${enc(eventId)}`),
  parts: (eventId, { page, search, category } = {}) => request('GET', `/events/${enc(eventId)}/parts`, {
    params: { page, search, category },
  }),
  // The bid screen (slice 2B).
  draft: (eventId) => request('GET', `/events/${enc(eventId)}/draft`),
  saveDraft: (eventId, body) => request('PUT', `/events/${enc(eventId)}/draft`, { body }),
  submit: (eventId, draftRevision, key) => request('POST', `/events/${enc(eventId)}/submit`, {
    body: { draft_revision: draftRevision }, key,
  }),
  withdraw: (eventId, { partNumbers, reason }, key) => request('POST', `/events/${enc(eventId)}/withdraw`, {
    body: partNumbers ? { part_numbers: partNumbers, reason } : { reason }, key,
  }),
  decline: (eventId, reason, key) => request('POST', `/events/${enc(eventId)}/decline`, {
    body: { reason }, key,
  }),
  // Changes the buyer published during a round (slice 2E).
  amendment: (eventId, amendmentId, page) => request('GET',
    `/events/${enc(eventId)}/amendments/${enc(amendmentId)}`, { params: { page } }),
  acknowledge: (eventId, amendmentId, key) => request('POST',
    `/events/${enc(eventId)}/amendments/${enc(amendmentId)}/acknowledge`, { key }),
  // Documents the buyer shares (slice 2E-2).
  document: (eventId, documentId) => request('GET',
    `/events/${enc(eventId)}/documents/${enc(documentId)}`, { blob: true }),
  bidFile: (eventId) => request('GET', `/events/${enc(eventId)}/bid-file`, { blob: true }),
  // Lane 1440: the buyer's guide to bidding (present while the buyer's switch is on).
  bidGuide: (eventId) => request('GET', `/events/${enc(eventId)}/bid-guide.pdf`, { blob: true }),
  // `preview: true` checks the file and returns its rows without saving
  // (the supplier's check step, Lane 1394). Apply sends the same file without it.
  uploadBidFile: (eventId, file, draftRevision, { preview = false } = {}) => {
    const form = new FormData();
    form.append('file', file);
    form.append('draft_revision', String(draftRevision));
    if (preview) form.append('preview', 'true');
    return request('POST', `/events/${enc(eventId)}/bid-file`, { form });
  },
};

// Password rules, checked before sending (the server checks them again).
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 200;
